
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WorkflowCard from "@/components/WorkflowCard";
import {
  User,
  Settings,
  Package,
  FileText,
  ShoppingCart,
  Heart,
  BarChart3
} from "lucide-react";

// Mock user data
const mockUser = {
  id: "user1",
  name: "Alex Rivera",
  email: "alex@example.com",
  avatar: "AR",
  bio: "Automation expert specializing in sales and marketing workflows. I help businesses save time and increase productivity.",
  location: "San Francisco, CA",
  joinDate: "January 2023",
  isSeller: true,
  purchased: [
    {
      id: "wf2",
      title: "Social Media Content Calendar",
      description: "Schedule and organize your social media content across multiple platforms.",
      price: 19.99,
      rating: 4.5,
      reviewCount: 97,
      platform: "Make.com",
      category: "Marketing",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    },
    {
      id: "wf4",
      title: "Invoice Processing Automation",
      description: "Automatically process and track invoices with this accounting workflow.",
      price: 39.99,
      rating: 4.7,
      reviewCount: 78,
      platform: "Zapier",
      category: "Finance",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    }
  ],
  created: [
    {
      id: "wf1",
      title: "Customer Onboarding Automation",
      description: "Automate your customer onboarding process with this comprehensive Zapier workflow.",
      price: 29.99,
      rating: 4.8,
      reviewCount: 124,
      platform: "Zapier",
      category: "Sales",
      image: "https://images.unsplash.com/photo-1518893494013-481c1d8ed3fd?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    },
    {
      id: "wf6",
      title: "Project Management Dashboard",
      description: "Track project progress and team performance with this automated dashboard.",
      price: 49.99,
      rating: 4.6,
      reviewCount: 45,
      platform: "Notion",
      category: "Operations",
      image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    }
  ],
  favorited: [
    {
      id: "wf5",
      title: "Lead Qualification System",
      description: "Score and qualify leads automatically based on custom criteria.",
      price: 24.99,
      rating: 4.3,
      reviewCount: 112,
      platform: "Make.com",
      category: "Sales",
      image: "https://images.unsplash.com/photo-1550305080-4e029753abcf?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    }
  ],
  sellerStats: {
    totalRevenue: 1245.75,
    totalSales: 42,
    averageRating: 4.7
  }
};

const Profile = () => {
  const [user, setUser] = useState(mockUser);
  
  useEffect(() => {
    // Update page title
    document.title = `${user.name} - FlowMarket`;
    
    // Fetch user data in a real app
    console.log("Fetching user profile data");
    
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left sidebar */}
          <div className="md:col-span-1">
            <div className="bg-card rounded-lg border border-border p-6 mb-6">
              <div className="flex flex-col items-center text-center mb-6">
                <Avatar className="h-24 w-24 mb-4">
                  <AvatarImage src="" alt={user.name} />
                  <AvatarFallback className="text-2xl">{user.avatar}</AvatarFallback>
                </Avatar>
                
                <h1 className="text-xl font-bold">{user.name}</h1>
                <p className="text-sm text-muted-foreground mb-2">{user.email}</p>
                <p className="text-sm text-muted-foreground">Joined {user.joinDate}</p>
                
                {user.isSeller && (
                  <Badge className="mt-3" variant="secondary">Verified Seller</Badge>
                )}
              </div>
              
              <div className="space-y-4 mb-6">
                <p className="text-sm">{user.bio}</p>
                <p className="text-sm flex items-center gap-2">
                  <span className="text-muted-foreground">Location:</span>
                  <span>{user.location}</span>
                </p>
              </div>
              
              <div className="flex justify-center">
                <Button variant="outline" size="sm" className="w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            </div>
            
            {user.isSeller && (
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-muted-foreground" />
                  <span>Seller Stats</span>
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Total Revenue</span>
                    <span className="font-medium">${user.sellerStats.totalRevenue.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Total Sales</span>
                    <span className="font-medium">{user.sellerStats.totalSales}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Average Rating</span>
                    <span className="font-medium">{user.sellerStats.averageRating}/5</span>
                  </div>
                </div>
                
                <Button className="w-full mt-6">
                  Seller Dashboard
                </Button>
              </div>
            )}
          </div>
          
          {/* Main content */}
          <div className="md:col-span-2">
            <Tabs defaultValue="purchased">
              <TabsList className="mb-6 grid grid-cols-3 md:grid-cols-4 w-full">
                <TabsTrigger value="purchased" className="flex items-center gap-1 text-xs md:text-sm">
                  <ShoppingCart className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Purchased</span>
                </TabsTrigger>
                {user.isSeller && (
                  <TabsTrigger value="created" className="flex items-center gap-1 text-xs md:text-sm">
                    <Package className="h-4 w-4 md:mr-1" />
                    <span className="hidden md:inline">Created</span>
                  </TabsTrigger>
                )}
                <TabsTrigger value="favorites" className="flex items-center gap-1 text-xs md:text-sm">
                  <Heart className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Favorites</span>
                </TabsTrigger>
                <TabsTrigger value="info" className="flex items-center gap-1 text-xs md:text-sm">
                  <User className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Account</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="purchased">
                <h2 className="text-2xl font-bold mb-6">Purchased Workflows</h2>
                
                {user.purchased.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {user.purchased.map((workflow) => (
                      <WorkflowCard key={workflow.id} {...workflow} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-muted/50 rounded-lg">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No purchased workflows yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Explore the marketplace to find workflow templates that can save you time.
                    </p>
                    <Button>Browse Marketplace</Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="created">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Created Workflows</h2>
                  <Button>
                    Create New
                  </Button>
                </div>
                
                {user.created.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {user.created.map((workflow) => (
                      <WorkflowCard key={workflow.id} {...workflow} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-muted/50 rounded-lg">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No created workflows yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Start sharing your automation expertise by selling your workflows.
                    </p>
                    <Button>Create Workflow</Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="favorites">
                <h2 className="text-2xl font-bold mb-6">Favorite Workflows</h2>
                
                {user.favorited.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {user.favorited.map((workflow) => (
                      <WorkflowCard key={workflow.id} {...workflow} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-muted/50 rounded-lg">
                    <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No favorite workflows yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Save workflows you like to come back to them later.
                    </p>
                    <Button>Browse Marketplace</Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="info">
                <h2 className="text-2xl font-bold mb-6">Account Information</h2>
                
                <div className="bg-card rounded-lg border border-border p-6 mb-6">
                  <div className="space-y-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Full Name</span>
                      <span>{user.name}</span>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Email Address</span>
                      <span>{user.email}</span>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Location</span>
                      <span>{user.location}</span>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Member Since</span>
                      <span>{user.joinDate}</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 space-y-4">
                    <Button variant="outline" className="w-full justify-start">
                      <Settings className="mr-2 h-4 w-4" />
                      Account Settings
                    </Button>
                    
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="mr-2 h-4 w-4" />
                      Billing History
                    </Button>
                    
                    <Button variant="outline" className="w-full justify-start text-destructive hover:text-destructive">
                      <Settings className="mr-2 h-4 w-4" />
                      Log Out
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Profile;
